// @flow
import type {Node} from 'react';
import type {ViewProps} from 'bfu-auth-client/src/login/types/Types.js';

import React from 'react';

import Box from 'react-goose/lib/position/Box';
import Button from 'react-goose/lib/Button';

import useParcelForm from 'react-dataparcels/useParcelForm';
import validation from 'react-dataparcels/validation';

import Loader from 'bfu-auth-client/src/affordance/Loader.jsx';
import BlueflagAuth from 'bfu-auth-client/src/login/data/BlueflagAuth.js';
import StatusMessage from 'bfu-auth-client/src/login/appliance/StatusMessage.jsx';
import createStatusFromParcel from 'bfu-auth-client/src/login/data/createStatusFromParcel.js';
import {usernameValidator} from 'bfu-auth-client/src/login/data/Validators.js';
import {passwordValidator} from 'bfu-auth-client/src/login/data/Validators.js';
import {UsernameField} from 'bfu-auth-client/src/login/appliance/Fields.jsx';
import {PasswordField} from 'bfu-auth-client/src/login/appliance/Fields.jsx';
import {useEffect, useState} from 'react';

const SignInView = (props: ViewProps): Node => {
    let {config, links, next, status, context, forceUsernamePassword} = props;
    const urlParams = new URLSearchParams(window.location.search);
    const noAutoRedirect = urlParams.get('noAutomaticRedirect');
    const [authEndpoints, setAuthEndpoints] = useState({});
    const [userPassEnabled, setUserPassEnabled] = useState(forceUsernamePassword);
    const [providersEnabled, setProvidersEnabled] = useState(false);
    useEffect(()=>{
        BlueflagAuth()
            .getAuthEndpoint()
            .then(result => {
                setAuthEndpoints(result.authEndpoint);
                let withProviders = Array.isArray(result.authEndpoint.providers) && result.authEndpoint.providers.length > 0;
                setProvidersEnabled(withProviders);
                setUserPassEnabled(!withProviders);
            })
            .catch(() => {
                setAuthEndpoints({error: "Failed to fetch auth endpoints."});
                setUserPassEnabled(true);
                setProvidersEnabled(false);
            });
    } , []);

    let SignupLink = links.signup;
    let ForgotPasswordLink = links.forgotPassword;
    let ConfirmLink = links.confirm;

    let [signinParcel, signinControl] = useParcelForm({
        value: () => ({
            username: context.username || '',
            password: ''
        }),
        onChange: async (parcel) => {
            let {username, password} = parcel.value;
            return BlueflagAuth()
                .signIn(username, password)
                .then(() => next('SIGNIN_SUCCESS'))
                .catch(err => {
                    if(err.name === 'UserNotConfirmedException') {
                        next('SIGNUP_CONFIRM_RESEND', {username});
                        return;
                    }
                    return Promise.reject(err);
                });
        },
        validation: () => validation({
            'username': usernameValidator,
            'password': passwordValidator
        })
    });

    
    status = createStatusFromParcel(signinControl, status);
    if(signinControl.onChangeStatus.isPending) {
        return <Loader modifier="sizeMega center" text={config.signin.loadingText} />;
    }

    let submit = (e) => {
        e.preventDefault();
        signinControl.submit();
    };

    if(!userPassEnabled && providersEnabled && authEndpoints.providers.length === 1 && !noAutoRedirect) {
        // if only one provider is available and user/pass is not enabled
        // just redirect to the provider
        window.location.href = authEndpoints.providers[0].authorization_endpoint;
        return <Loader modifier="sizeMega center" text={config.signin.loadingText} />;
    }
    return <div className="BlueflagSignInViewWrapper">
        <div style={{width: "300px"}}>
            {config.app.logo &&
                <img
                    className="BlueflagAuthFormLogo"
                    src={config.app.logo}
                    title={config.app.name}
                    alt={config.app.name}
                />
            }
        </div>
        <div className="BlueflagAuthFormVerticalDivider"/>
        <div style={{width: "300px"}}>
            {providersEnabled && authEndpoints.providers.map((item) => {
                return <Button 
                    spruceName="BlueflagAuthFormButton" 
                    modifier="secondary withIcon" 
                    key={item.providerName}
                    onClick={()=> {window.location.href = item.authorization_endpoint;}}>
                    <svg width="22px" height="22px" viewBox="0 0 16 16" fill="none"><path fill="#F35325" d="M1 1h6.5v6.5H1V1z"></path><path fill="#81BC06" d="M8.5 1H15v6.5H8.5V1z"></path><path fill="#05A6F0" d="M1 8.5h6.5V15H1V8.5z"></path><path fill="#FFBA08" d="M8.5 8.5H15V15H8.5V8.5z"></path></svg>
                    <span>{item.providerName} Sign In</span>
                </Button>;
            })}
            {!userPassEnabled && <Button
                spruceName="BlueflagAuthFormButton"
                modifier="secondary withIcon"
                onClick={() => {setUserPassEnabled(true);}}
            >
                <svg width="22" height="22" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <title>User Authentication Icon - Blue Person, Gold Lock</title>
                    <circle cx="30" cy="35" r="12" fill="#1E90FF"/> {/* Person Head - Dodger Blue */}
                    <path d="M12 80 C12 55, 20 50, 30 50 C40 50, 48 55, 48 80Z" fill="#1E90FF"/> {/* Person Body/Jumper - Dodger Blue */}
                    <path d="M60 45 V30 C60 18, 85 18, 85 30 V45" fill="none" stroke="#B8860B" strokeWidth="6" strokeLinecap="round"/>
                    <rect x="55" y="45" width="35" height="35" rx="7" fill="#FFD700" stroke="#B8860B" strokeWidth="3"/>
                </svg>
                <span>Use Username/Password</span>
            </Button>}
            
            {userPassEnabled && 
                <>
                    <StatusMessage status={status} />
                    <form onSubmit={submit} method="post">
                        <UsernameField
                            parcel={signinParcel.get('username')}
                            fieldsConfig={config.fields}
                        />
                        <PasswordField
                            parcel={signinParcel.get('password')}
                            fieldsConfig={config.fields}
                        />
                        <Box spruceName="BlueflagAuthFormButtonContainer">
                            <Button spruceName="BlueflagAuthFormButton" modifier="primary" type="submit">{config.signin.submitButton}</Button>
                            <SignupLink>{config.signin.signupLink}</SignupLink>
                            <ForgotPasswordLink>{config.signin.forgotPasswordLink}</ForgotPasswordLink>
                            <ConfirmLink>{config.signin.confirmLink}</ConfirmLink>
                        </Box>
                    </form>
                </>}
            {!userPassEnabled && !providersEnabled &&
                <Loader modifier="sizeMega center" text={config.signin.loadingText} />}
        </div>
    </div>;
};

export default SignInView;